import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { SerializedError } from "@reduxjs/toolkit";
import { ERROR_CODES as PandoraErrorCodes } from "@loopscale/pandora-types";

// Pandora returns these but the pinned @loopscale/pandora-types predates them; delete once the package is bumped.
export enum UnpublishedPandoraErrorCodes {
    EXPONENT_ROLLOVER_INVALID_DESTINATION = 3001,
    EXPONENT_ROLLOVER_DESTINATION_MATURED = 3002,
    EXPONENT_ROLLOVER_INSUFFICIENT_DEPTH = 3003,
    NO_QUOTE_AVAILABLE = 4001
}

export type PandoraErrorCode = PandoraErrorCodes | UnpublishedPandoraErrorCodes;

const KNOWN_PANDORA_ERROR_CODES = new Set<unknown>([
    ...Object.values(PandoraErrorCodes),
    ...Object.values(UnpublishedPandoraErrorCodes)
]);

function isPandoraErrorCode(code: unknown): code is PandoraErrorCode {
    return typeof code === "number" && KNOWN_PANDORA_ERROR_CODES.has(code);
}

export function getPandoraErrorCode(
    error: FetchBaseQueryError | SerializedError | undefined
): PandoraErrorCode | undefined {
    if (!error || !("data" in error)) return undefined;
    const code = getEnvelopeErrorCode(error.data);
    return isPandoraErrorCode(code) ? code : undefined;
}

// Pandora's handleError responds with `{ error: { code, message } }`.
function getEnvelopeErrorCode(data: unknown): unknown {
    if (!data || typeof data !== "object" || !("error" in data)) return undefined;
    const { error } = data;
    if (!error || typeof error !== "object" || !("code" in error)) return undefined;
    return error.code;
}

export function getPandoraErrorMessage(error: FetchBaseQueryError | SerializedError, fallbackMessage: string): string {
    const errorCode = getPandoraErrorCode(error);
    switch (errorCode) {
        case PandoraErrorCodes.JUPITER_NO_ROUTES_FOUND:
            return "Unable to find a swap route through our integration partners.";
        case PandoraErrorCodes.RATEX_NO_TICKS_FOUND:
            return "No rollover liquidity currently available. Please try again later or close your position.";
        case UnpublishedPandoraErrorCodes.NO_QUOTE_AVAILABLE:
            return "Not enough liquidity to swap this amount. Try a smaller amount.";
        case UnpublishedPandoraErrorCodes.EXPONENT_ROLLOVER_INSUFFICIENT_DEPTH:
            return "Not enough rollover liquidity for this position size right now. Please try again later or close your position.";
        case UnpublishedPandoraErrorCodes.EXPONENT_ROLLOVER_DESTINATION_MATURED:
            return "The market you are rolling into has matured. Please choose a newer market.";
        default:
            return fallbackMessage;
    }
}
